import { Link } from 'react-router-dom';
import { useApiQuery } from '../use-api.js';
import {
  ALARMS_PATH,
  ALARMS_REFETCH_MS,
  firingHeadline,
  isPartialListing,
  type AlarmsResponse,
} from '../lib/alarms.js';

/**
 * gh#351: the push-shaped half of a pull-only surface.
 *
 * A dedicated /alarms page alone would be one more place nobody looks — the
 * exact property that let the diff-vader#223 collapse run ten weeks. This strip
 * is mounted on every authenticated page, so a firing alarm is seen on the next
 * visit to any part of the portal rather than only by someone who thought to
 * check.
 *
 * It shows nothing on a healthy day (chrome that is always present stops being
 * read), and nothing while the first fetch is in flight (a flash on every
 * navigation is worse than a 400ms delay). It does NOT stay silent on either way
 * of failing to know: an unreachable endpoint, or a listing the endpoint itself
 * reports as incomplete. Rendering either as all-clear would reproduce, in the
 * surface, the same fail-open the alarms themselves were guilty of.
 *
 * ALARM only. INSUFFICIENT_DATA is deliberately left to the /alarms page: it is
 * also the normal state of a freshly created alarm, so bannering it would cry
 * wolf after every deploy that touches one. The alarm where "no data" is the
 * real danger — CouncilBlockingSharePublisherSilentAlarm — is configured
 * treatMissingData: BREACHING precisely so that silence arrives here as ALARM.
 */
export function AlarmBanner() {
  const { data, isLoading, error } = useApiQuery<AlarmsResponse>(
    ['alarms'],
    ALARMS_PATH,
    { refetchInterval: ALARMS_REFETCH_MS },
  );

  // role="status" (polite), not role="alert": a degraded read path is not an
  // emergency, but it must not be silent to a screen reader either. The strip is
  // inserted dynamically by polling, so without a live region it would appear
  // with no announcement at all — leaving the fail-open warning visible to
  // sighted users only.
  if (error) {
    return (
      <div role="status" style={{ ...stripStyle, background: 'var(--loop-neutral-200)', color: 'var(--loop-neutral-700)', borderBottom: '1px solid var(--loop-neutral-300)' }}>
        <span aria-hidden="true">◌</span>
        <span>Alarm state unavailable — this banner is not reporting.</span>
        <Link to="/alarms" style={{ ...linkStyle, color: 'var(--loop-neutral-700)' }}>Retry on the alarms page →</Link>
      </div>
    );
  }

  if (isLoading || !data) return null;

  const firing = data.alarms.filter((a) => a.state === 'ALARM');

  // Firing wins over partial: if we know something is alight, that is the
  // headline whether or not the listing was complete.
  //
  // But a partial listing with nothing firing must NOT fall through to the
  // silent branch below. `in_alarm_count` would be 0 only because of the part we
  // did not receive, and treating that as all-clear is precisely the fail-open
  // the /alarms page already refuses — the banner has to refuse it too.
  if (data.in_alarm_count === 0) {
    if (isPartialListing(data)) {
      return (
        <div role="status" style={{ ...stripStyle, background: 'var(--loop-neutral-200)', color: 'var(--loop-neutral-700)', borderBottom: '1px solid var(--loop-neutral-300)' }}>
          <span aria-hidden="true">◌</span>
          <span>Alarm listing incomplete — nothing firing in the part that was read.</span>
          <Link to="/alarms" style={{ ...linkStyle, color: 'var(--loop-neutral-700)' }}>See what is missing →</Link>
        </div>
      );
    }
    return null;
  }

  return (
    <div
      role="alert"
      style={{ ...stripStyle, background: 'var(--loop-red-100)', color: 'var(--loop-red-700)', borderBottom: '1px solid var(--loop-red-200)' }}
    >
      <span aria-hidden="true">▲</span>
      <strong>{firingHeadline(data.in_alarm_count)}</strong>
      {/* Names, not just a count — "3 alarms firing" tells you to click; the
          names often tell you whether it is a stuck queue or a review-quality
          regression before you do. */}
      <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {firing.map((a) => a.name).join(', ')}
      </span>
      <Link to="/alarms" style={linkStyle}>View details →</Link>
    </div>
  );
}

const stripStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--loop-space-100)',
  padding: '8px var(--loop-space-300)',
  fontSize: '0.875rem',
} as const;

const linkStyle = {
  marginLeft: 'auto',
  flexShrink: 0,
  color: 'var(--loop-red-700)',
  fontWeight: 528,
  textDecoration: 'none',
} as const;
